import React, { useEffect, useMemo, useState } from "react";
import BreadcrumbComponent from "../../common/components/breadcrumb-component/breadcrumb-component";
import "./personal-settings.scss";
import "./company-settings.scss";
import phoneIcon from "../../assets/icons/phone.svg";
import emailIcon from "../../assets/icons/mail.svg";
import dummyProfileIcon from "../../assets/icons/smiling-corporate-male.svg";
import { ErrorMessage, Field, Formik } from "formik";
import * as Yup from "yup";
import SearchableAutoSelect from "../../common/components/auto-select-search-component/search-select-component";
import { getTimezones } from "../../constants/timezones";
import {
  getEmailServiceOptions,
  getSelfLinkedPartnerStatus,
  getTenantProfile,
  selfLinkWithPartner,
  updateProfileDetails,
} from "../../api/settings";
import {
  AlertComponent,
  CountryCodeWithPhoneNumber,
  PasswordInputComponent,
  SelectInputComponent,
} from "../../common/components";
import { getCurrentTenantId } from "../../utils/utils";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useLogin } from "../../common/hooks/useUser";
import { ROLES } from "../../utils/constants";
import EqualWidthList from "../../common/components/equal-width-list/EqualWidthList";
import AddressFormFields from "../../common/components/address-form-fields/address-form-fields";
import {
  emailValidationRegex,
  websiteUrlRegex,
} from "../../constants/regexStrings";
import { debounce } from "lodash";
import { useSetLoading } from "../../common/hooks/appearance";
import { validatePartnerCompany } from "../../api/tenets";
import icons from "../../assets/icons/icons";
import MUIBoxTable from "../../common/components/mui-table/mui-box-table";

const companySchema = Yup.object({
  companyName: Yup.string().required("Company name is required"),
  companyWebsite: Yup.string().matches(
    websiteUrlRegex,
    "Enter a valid website URL",
  ),
  timezone: Yup.string().required("Timezone is required"),
  employeeCount: Yup.string(),
  // products: Yup.array().of(
  //   Yup.object().shape({
  //     label: Yup.string(),
  //     value: Yup.string(),
  //   })
  // ),
});

const addressSchema = Yup.object({
  streetLine1: Yup.string()
    .nullable()
    .min(3, "Street line 1 must be at least 3 characters")
    .max(100, "Street line 1 cannot be more than 100 characters"),
  streetLine2: Yup.string()
    .nullable()
    .min(3, "Street line 2 must be at least 3 characters")
    .max(100, "Street line 2 cannot be more than 100 characters"),
  city: Yup.string(),
  postcode: Yup.string(),
  state: Yup.string(),
  country: Yup.string(),
});

const emailSetupSchema = Yup.object({
  email: Yup.string()
    .matches(emailValidationRegex, "Please enter a valid Email address")
    .required("Email cannot be empty")
    .max(150, "Email cannot be more than 150 characters"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .max(30, "Password length must be less than 30 characters")
    .required("Password is required"),
  emailService: Yup.string().required("Email service is required"),
});
const twilioSetupSchema = Yup.object({
  twilioAccountSID: Yup.string().required("Twilio Account SID is required"),
  twilioAuthToken: Yup.string().required("Twilio Auth Token is required"),
  twilioPhoneNumber: Yup.string().required("Twilio phone number is required"),
});
const PRODUCT_OPTIONS = [
  { label: "At-Need", value: "At-Need" },
  { label: "Pre-Need", value: "Pre-Need" },
];

const CompanySettings: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExistCompanyName, setIsExistCompanyName] = useState<boolean>(false);
  const crumbMapping = [{ label: "Settings", link: "/settings" }];
  const timezones = getTimezones();
  const [profile, setProfile] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [isEmailSetupEditing, setIsEmailSetupEditing] = useState(false);
  const [isTwilioSetupEditing, setIsTwilioSetupEditing] = useState(false);
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: string;
    type?: string;
  }>({});

  const [showPassword, setShowPassword] = useState(false);
  const [showTwilioAuthToken, setShowTwilioAuthToken] = useState(false);
  const [emailServiceOptions, setEmailServiceOptions] = useState<any[]>([]);
  const [linkedPartner, setLinkedPartner] = useState<any>(null);
  const [confirmationPopupOpen, setConfirmationPopupOpen] = useState(false);
  const [partnerDetails, setPartnerDetails] = useState<any>(null);

  const loginData = useLogin();

  const toggleVisibility = () => setShowPassword((prev) => !prev);
  const toggleTwilioAuthTokenVisibility = () =>
    setShowTwilioAuthToken((prev) => !prev);

  const tenantId = getCurrentTenantId();

  const validateCompanyName = async (companyName: string) => {
    try {
      setIsLoading(true);
      const resp: any = await validatePartnerCompany({
        companyName: companyName.trim(),
      });

      if (resp?.data?.data?.exists) {
        setIsExistCompanyName(true);
      } else {
        setIsExistCompanyName(false);
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      console.error("Validation error:", e);
    }
  };

  const debouncedValidate = useMemo(
    () => debounce(validateCompanyName, 500),
    [],
  );

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const fetchEmailServiceOptions = async () => {
    try {
      setIsLoading(true);
      const response = await getEmailServiceOptions();
      setEmailServiceOptions(response.data?.data);
    } catch (error: any) {
      console.error("Error fetching email service options:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await getTenantProfile({ id: tenantId ?? "" });
      setProfile(response.data?.data);
      setIsLoading(false);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to fetch user profile",
        type: "error",
      });
      setIsLoading(false);
      console.error("Error fetching user profile:", error);
    }
  };

  const getMyLinkedPartnerStatus = async () => {
    try {
      setIsLoading(true);
      const response = await getSelfLinkedPartnerStatus(tenantId ?? 0);
      setLinkedPartner(response.data?.data);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message ||
          "Failed to fetch linked partner status",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkPartner = async (confirmation: boolean = false) => {
    try {
      setIsLoading(true);
      const response = await selfLinkWithPartner(tenantId ?? 0, {
        confirmation,
      });
      if (confirmation) {
        await getMyLinkedPartnerStatus();
        setAlert({
          isOpen: true,
          message: response?.data?.message || "Partner linked successfully",
          type: "success",
        });
        return;
      }
      setPartnerDetails(response.data?.data?.partner);
      setConfirmationPopupOpen(true);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error?.response?.data?.message || "Failed to link partner",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
    fetchEmailServiceOptions();
    getMyLinkedPartnerStatus();
  }, [tenantId]);

  const initialCompanyValues = {
    companyName: profile?.basic_details?.company_name || "",
    companyWebsite: profile?.basic_details?.company_website || "",
    timezone: profile?.basic_details?.timezone || "",
    employeeCount: profile?.basic_details?.employee_count?.toString() || "",
    products: profile?.basic_details?.products || "",
  };

  const initialAddressValues = {
    streetLine1: profile?.company_address?.street_line_1,
    streetLine2: profile?.company_address?.street_line_2,
    city: profile?.company_address?.city || "",
    postcode: profile?.company_address?.postcode || "",
    state: profile?.company_address?.state || "",
    country: profile?.company_address?.country || "",
  };

  const initialEmailSetupValues = {
    email: profile?.accountDetails?.email || "",
    password: profile?.accountDetails?.password || "",
    emailService: profile?.accountDetails?.emailService || "",
  };
  const initialTwilioSetupValues = {
    twilioAccountSID: profile?.twilioDetails?.twilioAccountSID || "",
    twilioAuthToken: profile?.twilioDetails?.twilioAuthToken || "",
    twilioPhoneNumber: profile?.twilioDetails?.twilioPhoneNumber || "",
  };

  const handleCompanySubmit = async (
    values: typeof initialCompanyValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    const companyPayload = {
      companyName: values.companyName,
      companyWebsite: values.companyWebsite,
      timezone: values.timezone,
      employeeCount: Number(values.employeeCount),
      products: values.products || [],
    };
    try {
      const response = await updateProfileDetails(
        Number(tenantId),
        "basicDetails",
        companyPayload,
      );
      if (response.data.success) {
        setProfile((prev: any) => ({
          ...prev,
          basic_details: {
            ...prev?.basic_details,
            company_name: values.companyName,
            company_website: values.companyWebsite,
            timezone: values.timezone,
            employee_count: Number(values.employeeCount),
            products: values.products || [],
          },
        }));
        setIsEditing(false);
        setAlert({
          isOpen: true,
          message:
            response?.data?.message || "Company details updated successfully",
          type: "success",
        });
      }
    } catch (error: any) {
      console.error("Error updating company details:", error);
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to update company details",
        type: "error",
      });
    }
    resetForm();
  };

  const formatEmailService = (service: string) => {
    return service
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleAddressSubmit = async (values: typeof initialAddressValues) => {
    const addressPayload = {
      id: profile?.company_address?.id,
      streetLine1: values.streetLine1,
      streetLine2: values.streetLine2,
      city: values.city,
      state: values.state,
      postcode: values.postcode,
      country: values.country,
    };
    try {
      const response = await updateProfileDetails(
        Number(tenantId),
        "address",
        addressPayload,
      );
      if (response.data.success) {
        setProfile((prev: any) => ({
          ...prev,
          company_address: {
            ...prev?.company_address,
            ...addressPayload,
          },
        }));
        setAlert({
          isOpen: true,
          message:
            response?.data?.message || "Company address updated successfully",
          type: "success",
        });
        setIsAddressEditing(false);
      }
    } catch (error: any) {
      console.error("Error updating company address:", error);
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to update company address",
        type: "error",
      });
    }
  };

  const handleEmailSetupSubmit = async (
    values: typeof initialEmailSetupValues,
  ) => {
    try {
      const response = await updateProfileDetails(
        Number(tenantId),
        "emailAccountSetup",
        values,
      );
      if (response.data.success) {
        setProfile((prev: any) => ({
          ...prev,
          accountDetails: {
            ...values,
          },
        }));
        setAlert({
          isOpen: true,
          message:
            response?.data?.data?.message ||
            `Email setup completed successfully. Please inform all users in '${profile?.basic_details?.company_name}' to log in again to enable email-related features and exports.`,
          type: "success",
        });
        setIsEmailSetupEditing(false);
      }
    } catch (error: any) {
      console.error("Error updating Twilio setup:", error);
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to update Twilio setup",
        type: "error",
      });
    }
  };
  const handleTwilioSetupSubmit = async (
    values: typeof initialTwilioSetupValues,
  ) => {
    try {
      const response = await updateProfileDetails(
        Number(tenantId),
        "twilioAccountSetup",
        values,
      );
      if (response.data.success) {
        setProfile((prev: any) => ({
          ...prev,
          twilioDetails: {
            ...values,
          },
        }));
        setAlert({
          isOpen: true,
          message:
            response?.data?.data?.message ||
            `Twilio setup completed successfully. Please inform all users in '${profile?.basic_details?.company_name}' to log in again to enable Twilio-related features.`,
          type: "success",
        });
        setIsTwilioSetupEditing(false);
      }
    } catch (error: any) {
      console.error("Error updating Twilio setup:", error);
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to update Twilio setup",
        type: "error",
      });
    }
  };
  return (
    <div className="personal-settings-wrapper company-settings-wrapper">
      <BreadcrumbComponent breadcrumbLink={crumbMapping} />

      <div className="profile-card-wrapper">
        <img
          src={profile?.basic_details?.thumbnail_two_url || dummyProfileIcon}
          className="profile-icon"
          alt="profile"
        />
        <div className="profile-card">
          <h2>{profile?.basic_details?.company_name}</h2>
          <div className="contact-info">
            <img src={phoneIcon} alt="phone" />
            <p>{profile?.basic_details?.phoneNumber || "N/A"}</p>
            <div className="divider"></div>
            <img src={emailIcon} alt="email" />
            <p>{profile?.basic_details?.email || "N/A"}</p>
          </div>
        </div>
      </div>

      <Formik
        enableReinitialize
        initialValues={initialCompanyValues}
        validationSchema={companySchema}
        onSubmit={handleCompanySubmit}
        validateOnBlur={true}
      >
        {({
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          values,
          resetForm,
          touched,
          errors,
          isValid,
          dirty,
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <div className="details-card-wrapper">
                <div className="edit-details">
                  {!isEditing && (
                    <button
                      className="edit-details-btn"
                      onClick={() => setIsEditing(true)}
                      type="button"
                    >
                      Edit Details
                    </button>
                  )}
                </div>

                <div className="details">
                  <div className="name-details">
                    <div className="name">
                      <p>Company Name</p>
                      {isEditing ? (
                        <div className="name-fields">
                          <Field
                            name="companyName"
                            placeholder="Enter company name"
                            onChange={(e: any) => {
                              const val = e.target.value;
                              setFieldValue("companyName", val);

                              if (
                                !isLoading &&
                                profile?.basic_details?.company_name !== val &&
                                val
                              ) {
                                debouncedValidate(val);
                              } else {
                                setIsExistCompanyName(false);
                              }
                            }}
                          />
                          {touched.companyName && errors.companyName && (
                            <div className="error-message">
                              {typeof errors.companyName === "string" &&
                                errors.companyName}
                            </div>
                          )}
                          {isExistCompanyName ? (
                            <div className="error-message">
                              Company name already exists
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      ) : (
                        <p>{values.companyName}</p>
                      )}
                    </div>
                    <div className="name">
                      <p>Company Website</p>
                      {isEditing ? (
                        <div className="name-fields">
                          <Field
                            name="companyWebsite"
                            placeholder="Enter website"
                          />
                          {touched.companyWebsite && errors.companyWebsite && (
                            <div className="error-message">
                              {typeof errors.companyWebsite === "string" &&
                                errors.companyWebsite}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p>{values.companyWebsite}</p>
                      )}
                    </div>
                    <div className="name">
                      <p>Products</p>
                      {isEditing ? (
                        <div className="name-fields">
                          <SelectInputComponent
                            fieldName="products"
                            options={PRODUCT_OPTIONS}
                            value={values.products || []}
                            onChange={(e: any) => setFieldValue("products", e)}
                            placeholder="Select product"
                            multiple={true}
                            tabs={true}
                          />
                        </div>
                      ) : (
                        <p>
                          {values?.products?.length
                            ? values.products.join(", ")
                            : "-"}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="name-details">
                    <div className="name">
                      <p>Time Zone</p>
                      {isEditing ? (
                        <div className="name-fields">
                          <SearchableAutoSelect
                            fieldName="timezone"
                            optional
                            options={timezones}
                            placeholder="Select time zone"
                            isShowError={!values.timezone}
                            value={
                              timezones.find(
                                (opt) => opt.value === values.timezone,
                              ) || null
                            }
                            onChange={(e: any) => setFieldValue("timezone", e)}
                            handleBlur={() => setFieldTouched("timezone", true)}
                          />
                        </div>
                      ) : (
                        <p>{values.timezone || "-"}</p>
                      )}
                    </div>

                    <div className="name">
                      <p>Employee Count</p>
                      {isEditing ? (
                        <div className="name-fields">
                          <Field
                            name="employeeCount"
                            placeholder="Enter employee count"
                          />
                          {touched.employeeCount && errors.employeeCount && (
                            <div className="error-message">
                              {typeof errors.employeeCount === "string" &&
                                errors.employeeCount}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p>{values.employeeCount}</p>
                      )}
                    </div>
                  </div>
                </div>

                {isEditing && (
                  <div className="name-details-buttons">
                    <button
                      type="button"
                      className="cancelButton"
                      onClick={() => {
                        resetForm();
                        setIsEditing(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="submitButton"
                      disabled={!dirty || !isValid || isExistCompanyName}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            </form>
          );
        }}
      </Formik>

      <Formik
        enableReinitialize
        initialValues={initialAddressValues}
        validationSchema={addressSchema}
        onSubmit={handleAddressSubmit}
        validateOnBlur={true}
      >
        {({
          handleSubmit,
          values,
          resetForm,
          errors,
          touched,
          dirty,
          isValid,
        }) => {
          const hasAnyValue = Object.values(values).some(
            (val) => val !== null && val !== "" && val !== undefined,
          );
          return (
            <form onSubmit={handleSubmit}>
              <div className="details-card-wrapper address company-settings-warpper">
                <div className="edit-details company-address">
                  <h3>Company Address</h3>
                  {isAddressEditing ? (
                    <div className="name-details-buttons">
                      <button
                        type="button"
                        className="cancelButton"
                        onClick={() => {
                          resetForm();
                          setIsAddressEditing(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="submitButton"
                        disabled={!dirty || !isValid || !hasAnyValue}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      className="edit-details-btn"
                      onClick={() => setIsAddressEditing(true)}
                      type="button"
                    >
                      Edit Address
                    </button>
                  )}
                </div>

                <div className="details-wrapper  company-address">
                  {isAddressEditing ? (
                    <AddressFormFields isOutlineInputs />
                  ) : (
                    <Box className="tenant-basic-details-content" gap={3}>
                      <Box className="tenant-basic-details-content-header">
                        <EqualWidthList
                          label="Street Line 1"
                          labelValue={values?.streetLine1 || "-"}
                        />
                        <EqualWidthList
                          label="Street Line 2"
                          labelValue={values?.streetLine2 || "-"}
                        />
                        <EqualWidthList
                          label="State"
                          labelValue={values?.state || "-"}
                        />
                      </Box>

                      <Box className="tenant-basic-details-content-header">
                        <EqualWidthList
                          label="Country"
                          labelValue={values?.country || "-"}
                        />
                        <EqualWidthList
                          label="City"
                          labelValue={values?.city || "-"}
                        />
                        <EqualWidthList
                          label="Zip Code"
                          labelValue={values?.postcode || "-"}
                        />
                      </Box>
                    </Box>
                  )}
                </div>
              </div>
            </form>
          );
        }}
      </Formik>

      {loginData?.userDetails?.roles?.role === ROLES.SUPER_ADMIN && (
        <Formik
          enableReinitialize
          initialValues={initialEmailSetupValues}
          validationSchema={emailSetupSchema}
          onSubmit={handleEmailSetupSubmit}
          validateOnBlur
        >
          {({
            handleSubmit,
            setFieldValue,
            values,
            resetForm,
            dirty,
            isValid,
            touched,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="details-card-wrapper address">
                <div className="edit-details  company-address">
                  <h3>Email Account Setup</h3>
                  {isEmailSetupEditing ? (
                    <div className="name-details-buttons">
                      <button
                        type="button"
                        className="cancelButton"
                        onClick={() => {
                          resetForm();
                          setIsEmailSetupEditing(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="submitButton"
                        disabled={!dirty}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      className="edit-details-btn"
                      onClick={() => setIsEmailSetupEditing(true)}
                      type="button"
                    >
                      Edit Setup
                    </button>
                  )}
                </div>
                <Box className="alert-terms-of-service">
                  <img src={icons.TermOfServicesInfo} alt="img" />
                  <p>
                    To send emails through your <strong>Gmail</strong> or{" "}
                    <strong>Outlook</strong> account, please provide your email
                    address and an <strong>App Password</strong> (not your
                    regular password).
                    <br />
                    <br />
                    🔐 App Passwords are special one-time passwords used to
                    securely connect third-party apps to your email account
                    without exposing your actual password.
                    <br />
                    <br />
                    👉 Learn how to generate an App Password:
                    <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                      <li>
                        <a
                          href="https://support.google.com/accounts/answer/185833?hl=en"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1a73e8",
                            textDecoration: "underline",
                          }}
                        >
                          Google (Gmail) App Password Instructions
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://support.microsoft.com/en-us/account-billing/create-app-passwords-from-the-security-info-page-d8bc744a-ce3f-4d4d-89c9-eb38ab9d4137"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1a73e8",
                            textDecoration: "underline",
                          }}
                        >
                          Microsoft (Outlook) App Password Instructions
                        </a>
                      </li>
                    </ul>
                  </p>
                </Box>
                <div className="email-details">
                  <div className="name-details">
                    <div className="name">
                      <p>Email</p>
                      {isEmailSetupEditing ? (
                        <Box sx={{}}>
                          <Field
                            name="email"
                            type="email"
                            placeholder="Email"
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="error-message"
                          />
                        </Box>
                      ) : (
                        <p>{values?.email || "N/A"}</p>
                      )}
                    </div>
                  </div>
                  <div className="name-details">
                    <div className="name">
                      <p>Password</p>
                      {isEmailSetupEditing ? (
                        <>
                          <div className="emailPasswordFieldOutlinedInputBox">
                            <PasswordInputComponent
                              fieldName="password"
                              placeholder="Password "
                              optional={true}
                              value={values?.password || ""}
                              onChange={(e: any) => {
                                setFieldValue("password", e.target.value);
                              }}
                              maxlength="30"
                              className="emailPasswordFieldOutlinedInput"
                              sx={{
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#F7F7F7",
                                  borderWidth: "0px",
                                },
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">
                            {showPassword
                              ? values?.password
                              : "•".repeat(values?.password?.length) || "-"}
                          </Typography>
                          {values?.password && (
                            <IconButton size="small" onClick={toggleVisibility}>
                              {showPassword ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </div>
                  </div>
                  <div className="name-details">
                    <div className="name">
                      <p>Email Service</p>
                      {isEmailSetupEditing ? (
                        <div className="email-service-select">
                          <SelectInputComponent
                            fieldName="emailService"
                            options={emailServiceOptions}
                            value={
                              formatEmailService(values?.emailService || "") ||
                              ""
                            }
                            onChange={(e: any) =>
                              setFieldValue("emailService", e)
                            }
                            placeholder="Select Email Service"
                            isShowError={
                              touched.emailService && !values?.emailService
                            }
                            isRequired={true}
                            closeIcon
                            onClear={() => {
                              setFieldValue("emailService", "");
                            }}
                            handleBlur={() =>
                              setFieldTouched("emailService", true)
                            }
                          />
                        </div>
                      ) : (
                        <p>
                          {formatEmailService(values?.emailService || "") ||
                            "N/A"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      )}
      {loginData?.userDetails?.roles?.role === ROLES.SUPER_ADMIN && (
        <Formik
          enableReinitialize
          initialValues={initialTwilioSetupValues}
          validationSchema={twilioSetupSchema}
          onSubmit={handleTwilioSetupSubmit}
          validateOnBlur
        >
          {({
            handleSubmit,
            setFieldValue,
            values,
            resetForm,
            dirty,
            isValid,
            touched,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="details-card-wrapper address">
                <div className="edit-details  company-address">
                  <h3>Twilio Account Setup</h3>
                  {isTwilioSetupEditing ? (
                    <div className="name-details-buttons">
                      <button
                        type="button"
                        className="cancelButton"
                        onClick={() => {
                          resetForm();
                          setIsTwilioSetupEditing(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="submitButton"
                        disabled={!dirty}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      className="edit-details-btn"
                      onClick={() => setIsTwilioSetupEditing(true)}
                      type="button"
                    >
                      Edit Setup
                    </button>
                  )}
                </div>
                <div className="twilio-details">
                  <div className="field-details">
                    <div
                      className="field"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span style={{ minWidth: 100 }}>Account SID:</span>
                      {isTwilioSetupEditing ? (
                        <Box sx={{}}>
                          <Field
                            name="twilioAccountSID"
                            type="text"
                            placeholder="Account SID"
                          />
                          <ErrorMessage
                            name="twilioAccountSID"
                            component="div"
                            className="error-message"
                          />
                        </Box>
                      ) : (
                        <span
                          style={{
                            maxWidth: 220,
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            verticalAlign: "bottom",
                          }}
                        >
                          {values?.twilioAccountSID || "N/A"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="field-details">
                    <div
                      className="field"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span style={{ minWidth: 100 }}>Auth Token:</span>
                      {isTwilioSetupEditing ? (
                        <div className="twilioFieldOutlinedInputBox">
                          <PasswordInputComponent
                            fieldName="twilioAuthToken"
                            placeholder="Auth Token"
                            optional={true}
                            value={values?.twilioAuthToken || ""}
                            onChange={(e: any) => {
                              setFieldValue("twilioAuthToken", e.target.value);
                            }}
                            maxlength="30"
                            className="twilioFieldOutlinedInput"
                            sx={{
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#F7F7F7",
                                borderWidth: "0px",
                              },
                            }}
                          />
                        </div>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="body1"
                            style={{
                              maxWidth: 220,
                              display: "inline-block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "bottom",
                            }}
                          >
                            {showTwilioAuthToken
                              ? values?.twilioAuthToken
                              : "•".repeat(values?.twilioAuthToken?.length) ||
                                "-"}
                          </Typography>
                          {values?.twilioAuthToken && (
                            <IconButton
                              size="small"
                              onClick={toggleTwilioAuthTokenVisibility}
                            >
                              {showTwilioAuthToken ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </div>
                  </div>
                  <div className="phone-field-details">
                    <div
                      className="field"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span style={{ minWidth: 111 }}>Phone Number:</span>
                      {isTwilioSetupEditing ? (
                        <Box sx={{}}>
                          <CountryCodeWithPhoneNumber
                            fieldName="twilioPhoneNumber"
                            placeholder={
                              values?.twilioPhoneNumber
                                ? ""
                                : "Phone Number"
                            }
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any,
                            ) => {
                              const countryCode = country?.dialCode;
                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object",
                                );
                                return;
                              }

                              if (`+${countryCode}` == two) {
                                setFieldValue("twilioPhoneNumber", null);
                              } else {
                                setFieldValue("twilioPhoneNumber", two);
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("twilioPhoneNumber", true);
                            }}
                            value={values?.twilioPhoneNumber || "+1"}
                            defaultCountry="us"
                            optional={true}
                          />
                        </Box>
                      ) : (
                        <span>{values?.twilioPhoneNumber || "N/A"}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      )}
      {loginData?.userDetails?.roles?.role === ROLES.SUPER_ADMIN &&
        loginData.tenantDetails.tenantType === "Tenant" && (
          <div className="details-card-wrapper linked-partner-card">
            <div className="details-card-header linked-partner-header">
              <h3>Linked Partner</h3>
            </div>
            <div className="linked-partner-content">
              <div className="linked-partner-info">
                <span className="item-label">
                  {linkedPartner?.linked
                    ? linkedPartner?.partner?.businessType === "Preneed Planner"
                      ? "Preneed Planner Name:"
                      : "Provider Name:"
                    : "Linked Partner"}
                </span>
                {linkedPartner?.linked ? (
                  <span className="item-value">
                    {linkedPartner?.partner?.businessName}
                  </span>
                ) : (
                  <span
                    className="item-value-link"
                    onClick={() => handleLinkPartner()}
                  >
                    Link Partner
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

      <Dialog open={confirmationPopupOpen}>
        <DialogTitle>Confirm Self-Linking</DialogTitle>
        <DialogContent>
          <Typography>
            Below are the details of the partner you are trying to link. Please
            confirm if you want to proceed with linking this partner to your
            account.
          </Typography>
          <Box
            sx={{
              mt: 2,
            }}
            className="self-link-partner-search-result-table"
          >
            <MUIBoxTable
              columns={[
                { label: "Name", key: "businessName" },
                { label: "Email", key: "email" },
                { label: "Phone", key: "phoneNumber" },
              ]}
              data={partnerDetails ? [partnerDetails] : []}
            />
          </Box>
        </DialogContent>
        <DialogActions className="self-link-partner-dialog-actions">
          <Button
            onClick={() => setConfirmationPopupOpen(false)}
            className="cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmationPopupOpen(false);
              handleLinkPartner(true);
            }}
            className="submit-button"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </div>
  );
};

export default CompanySettings;
